// AjoutAdministrateur.jsx
import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  message,
  Row,
  Col,
  Breadcrumb,
  Select,
  Checkbox,
  Spin,
  Grid,
  Card,
  Typography,
  Space,
  Avatar,
  Tag,
  Tooltip,
  Alert,
} from 'antd';
import {
  RightOutlined,
  UserAddOutlined,
  UserOutlined,
  PhoneOutlined,
  SafetyCertificateOutlined,
  CrownOutlined,
  CustomerServiceOutlined,
  DollarOutlined,
  ShopOutlined,
  SettingOutlined,
  DashboardOutlined,
  TeamOutlined,
  CarOutlined,
  WalletOutlined,
  QuestionCircleOutlined,
  AppstoreOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { UserService, rolesService } from '../../common/services';

const { Title, Text } = Typography;

// Mapping des icônes par nom
const ICON_MAP = {
  crown: <CrownOutlined />,
  'customer-service': <CustomerServiceOutlined />,
  dollar: <DollarOutlined />,
  shop: <ShopOutlined />,
  setting: <SettingOutlined />,
  lock: <LockOutlined />,
};

// Fonction pour générer une couleur de fond à partir d'une couleur
const getBgColor = (color) => {
  if (!color) return '#fafafa';
  // Ajouter une transparence à la couleur
  return `${color}10`;
};

// Icônes pour les groupes de permissions
const GROUP_ICONS = {
  dashboard: <DashboardOutlined />,
  users: <TeamOutlined />,
  partners: <TeamOutlined />,
  vehicles: <CarOutlined />,
  drivers: <UserOutlined />,
  wallet: <WalletOutlined />,
  assistance: <QuestionCircleOutlined />,
  services: <AppstoreOutlined />,
  sync: <SafetyCertificateOutlined />,
};

const AjoutAdministrateur = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [roles, setRoles] = useState([]); // Rôles depuis la BDD
  const [rolesMap, setRolesMap] = useState({}); // Map pour accès rapide par name

  const [role, setRole] = useState();
  const [selectedPerms, setSelectedPerms] = useState([]);

  const [permList, setPermList] = useState([]);
  const [groupOrder, setGroupOrder] = useState([]);
  const [groupLabels, setGroupLabels] = useState({});

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const isTablet = screens.sm && !screens.lg;

  // Charge permissions & rôles depuis l'API
  useEffect(() => {
    (async () => {
      try {
        // Charger les permissions
        const permPayload = await rolesService.getPermissions();
        const permsData = permPayload?.data ?? permPayload;

        const list = Array.isArray(permsData?.list)
          ? permsData.list
          : (permsData?.all || []).map(k => ({ key: k, label: k, group: null, groupLabel: null }));

        setPermList(list);
        setGroupLabels(permsData?.groupLabels || {});

        const order = [...new Set(list.map(i => i.group).filter(Boolean))];
        setGroupOrder(order);

        // Charger les rôles depuis la BDD
        const rolesResponse = await rolesService.getForSelect();
        const rolesData = rolesResponse?.data || [];

        // Créer un map des rôles pour accès rapide
        const rMap = {};
        rolesData.forEach(r => {
          rMap[r.name] = r;
        });
        setRolesMap(rMap);
        setRoles(rolesData);

        // Définir le rôle par défaut (admin ou le premier disponible)
        const defaultRole = rolesData.find(r => r.name === 'admin') || rolesData[0];
        if (defaultRole) {
          setRole(defaultRole.name);
          setSelectedPerms(defaultRole.permissions || []);
          form.setFieldsValue({
            role: defaultRole.name,
            permissions: defaultRole.permissions || [],
          });
        }
      } catch (e) {
        console.error(e);
        message.error('Impossible de charger les permissions et rôles.');
      } finally {
        setLoading(false);
      }
    })();
  }, [form]);

  const handleRoleChange = (r) => {
    const selectedRole = rolesMap[r];
    const next = selectedRole?.permissions || [];
    setRole(r);
    setSelectedPerms(next);
    form.setFieldsValue({ role: r, permissions: next });
  };

  const handleSubmit = async (values) => {
    const fullPhone = `+225${values.phone}`;
    const selectedRole = rolesMap[role];
    const finalValues = {
      ...values,
      phone: fullPhone,
      role,
      permissions: values.permissions?.length ? values.permissions : (selectedRole?.permissions || []),
    };

    try {
      setSubmitting(true);
      await UserService.enregistrerUnAdministrateur(finalValues);
      message.success('Administrateur ajouté avec succès');
      navigate('/dashboard/gestion-admins');
    } catch (error) {
      console.error(error);
      message.error(error?.message || "Erreur lors de l'ajout de l'administrateur");
    } finally {
      setSubmitting(false);
    }
  };

  // Compter les permissions sélectionnées par groupe
  const getGroupPermCount = (groupKey) => {
    const groupPerms = permList.filter(p => p.group === groupKey);
    const selectedInGroup = groupPerms.filter(p => selectedPerms.includes(p.key));
    return { selected: selectedInGroup.length, total: groupPerms.length };
  };

  // Sélectionner/désélectionner toutes les permissions d'un groupe
  const toggleGroupPermissions = (groupKey) => {
    const groupPerms = permList.filter(p => p.group === groupKey).map(p => p.key);
    const allSelected = groupPerms.every(p => selectedPerms.includes(p));

    let newPerms;
    if (allSelected) {
      newPerms = selectedPerms.filter(p => !groupPerms.includes(p));
    } else {
      newPerms = [...new Set([...selectedPerms, ...groupPerms])];
    }

    setSelectedPerms(newPerms);
    form.setFieldsValue({ permissions: newPerms });
  };

  // Récupérer la configuration du rôle actuel depuis la BDD
  const currentRole = rolesMap[role] || {};
  const roleConfig = {
    label: currentRole.label || 'Rôle',
    icon: ICON_MAP[currentRole.icon] || <SettingOutlined />,
    color: currentRole.color || '#595959',
    bgColor: getBgColor(currentRole.color),
    description: currentRole.description || 'Permissions personnalisables',
  };

  return (
    <>
      {/* Breadcrumb */}
      <Breadcrumb
        separator={<RightOutlined />}
        items={[
          { title: <Link to="/dashboard">Accueil</Link> },
          { title: <Link to="/dashboard/gestion-admins">Administrateurs</Link> },
          { title: 'Nouvel administrateur' },
        ]}
        style={{ marginBottom: 16 }}
      />

      {loading ? (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
            <Spin size="large" tip="Chargement des permissions..." />
          </div>
        </Card>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ role, permissions: selectedPerms }}
        >
          <Row gutter={[24, 24]}>
            {/* Colonne gauche - Informations personnelles */}
            <Col xs={24} lg={10}>
              <Card
                title={
                  <Space>
                    <UserOutlined style={{ color: '#1890ff' }} />
                    <span>Informations personnelles</span>
                  </Space>
                }
                style={{ height: '100%' }}
                headStyle={{ borderBottom: '2px solid #1890ff' }}
              >
                <Form.Item
                  name="firstName"
                  label={<Text strong>Nom</Text>}
                  rules={[{ required: true, message: 'Le nom est requis' }]}
                  normalize={(v) => v?.toUpperCase()}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                    placeholder="Ex : AMIDOU"
                    size="large"
                    style={{ textTransform: 'uppercase' }}
                  />
                </Form.Item>

                <Form.Item
                  name="lastName"
                  label={<Text strong>Prénom</Text>}
                  rules={[{ required: true, message: 'Le prénom est requis' }]}
                  normalize={(v) => v?.toUpperCase()}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                    placeholder="Ex : BERMAN"
                    size="large"
                    style={{ textTransform: 'uppercase' }}
                  />
                </Form.Item>

                <Form.Item
                  name="phone"
                  label={<Text strong>Téléphone</Text>}
                  rules={[
                    { required: true, message: 'Le téléphone est requis' },
                    { pattern: /^[0-9]{10}$/, message: 'Le numéro doit contenir 10 chiffres' },
                  ]}
                >
                  <Input
                    addonBefore={
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span>🇨🇮</span> +225
                      </span>
                    }
                    placeholder="0102030405"
                    size="large"
                    maxLength={10}
                  />
                </Form.Item>

                {/* Sélection du rôle */}
                <Form.Item
                  name="role"
                  label={<Text strong>Rôle</Text>}
                  rules={[{ required: true, message: 'Le rôle est requis' }]}
                >
                  <Select
                    size="large"
                    onChange={handleRoleChange}
                    value={role}
                    disabled={roles.length === 0}
                    optionLabelProp="label"
                  >
                    {roles.map(r => {
                      const icon = ICON_MAP[r.icon] || <SettingOutlined />;
                      return (
                        <Select.Option key={r.name} value={r.name} label={r.label}>
                          <Space>
                            <span style={{ color: r.color || '#595959' }}>{icon}</span>
                            <span>{r.label}</span>
                          </Space>
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>

                {/* Aperçu du rôle sélectionné */}
                <Card
                  size="small"
                  style={{
                    backgroundColor: roleConfig.bgColor,
                    border: `1px solid ${roleConfig.color}20`,
                  }}
                >
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Space>
                      <Avatar
                        size={32}
                        style={{ backgroundColor: roleConfig.color }}
                        icon={roleConfig.icon}
                      />
                      <Text strong style={{ color: roleConfig.color }}>
                        {roleConfig.label}
                      </Text>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {roleConfig.description}
                    </Text>
                    <div style={{ marginTop: 8 }}>
                      <Tag color={roleConfig.color}>
                        {selectedPerms.length} permission{selectedPerms.length > 1 ? 's' : ''} active{selectedPerms.length > 1 ? 's' : ''}
                      </Tag>
                    </div>
                  </Space>
                </Card>
              </Card>
            </Col>

            {/* Colonne droite - Permissions */}
            <Col xs={24} lg={14}>
              <Card
                title={
                  <Row justify="space-between" align="middle">
                    <Space>
                      <LockOutlined style={{ color: '#52c41a' }} />
                      <span>Permissions</span>
                    </Space>
                    <Tag color="blue">
                      {selectedPerms.length} / {permList.length} sélectionnées
                    </Tag>
                  </Row>
                }
                headStyle={{ borderBottom: '2px solid #52c41a' }}
              >
                <Alert
                  message="Les permissions définissent les accès de l'administrateur"
                  description="Cliquez sur le titre d'un groupe pour sélectionner/désélectionner toutes ses permissions."
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                <Form.Item name="permissions">
                  <Checkbox.Group
                    style={{ width: '100%' }}
                    value={selectedPerms}
                    onChange={(vals) => {
                      setSelectedPerms(vals);
                      form.setFieldsValue({ permissions: vals });
                    }}
                  >
                    <Row gutter={[16, 16]}>
                      {groupOrder.map((g) => {
                        const items = permList.filter(p => p.group === g);
                        if (!items.length) return null;
                        const { selected, total } = getGroupPermCount(g);
                        const allSelected = selected === total;
                        const someSelected = selected > 0 && selected < total;

                        return (
                          <Col xs={24} md={12} key={g}>
                            <Card
                              size="small"
                              style={{
                                borderColor: allSelected ? '#52c41a' : someSelected ? '#faad14' : '#d9d9d9',
                                backgroundColor: allSelected ? '#f6ffed' : someSelected ? '#fffbe6' : '#fff',
                              }}
                              bodyStyle={{ padding: 12 }}
                            >
                              {/* Header du groupe */}
                              <div
                                onClick={() => toggleGroupPermissions(g)}
                                style={{
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom: 8,
                                  paddingBottom: 8,
                                  borderBottom: '1px solid #f0f0f0',
                                }}
                              >
                                <Space>
                                  <span style={{ color: '#1890ff' }}>
                                    {GROUP_ICONS[g] || <AppstoreOutlined />}
                                  </span>
                                  <Text strong style={{ fontSize: 13 }}>
                                    {groupLabels?.[g] || g}
                                  </Text>
                                </Space>
                                <Tooltip title={`${selected}/${total} permissions`}>
                                  <Tag
                                    color={allSelected ? 'success' : someSelected ? 'warning' : 'default'}
                                    style={{ marginRight: 0 }}
                                  >
                                    {allSelected && <CheckCircleOutlined style={{ marginRight: 4 }} />}
                                    {selected}/{total}
                                  </Tag>
                                </Tooltip>
                              </div>

                              {/* Permissions du groupe */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {items.map(p => (
                                  <Checkbox
                                    key={p.key}
                                    value={p.key}
                                    style={{
                                      marginLeft: 0,
                                      padding: '4px 8px',
                                      borderRadius: 4,
                                      backgroundColor: selectedPerms.includes(p.key) ? '#e6f7ff' : 'transparent',
                                    }}
                                  >
                                    <Text style={{ fontSize: 12 }}>{p.label}</Text>
                                  </Checkbox>
                                ))}
                              </div>
                            </Card>
                          </Col>
                        );
                      })}

                      {/* Permissions sans groupe (fallback) */}
                      {permList.some(p => !p.group) && (
                        <Col xs={24}>
                          <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>
                              <Space>
                                <AppstoreOutlined />
                                Autres permissions
                              </Space>
                            </div>
                            <Row gutter={[8, 8]}>
                              {permList.filter(p => !p.group).map(p => (
                                <Col key={p.key} xs={24} sm={12} md={8}>
                                  <Checkbox value={p.key}>{p.label}</Checkbox>
                                </Col>
                              ))}
                            </Row>
                          </Card>
                        </Col>
                      )}
                    </Row>
                  </Checkbox.Group>
                </Form.Item>
              </Card>
            </Col>
          </Row>

          {/* Boutons d'action */}
          <Card style={{ marginTop: 24 }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Text type="secondary">
                  Tous les champs marqués d'un astérisque (*) sont obligatoires
                </Text>
              </Col>
              <Col>
                <Space>
                  <Button
                    size="large"
                    onClick={() => navigate('/dashboard/gestion-admins')}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    loading={submitting}
                    icon={<SaveOutlined />}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                    }}
                  >
                    {submitting ? 'Enregistrement...' : 'Créer l\'administrateur'}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </Form>
      )}
    </>
  );
};

export default AjoutAdministrateur;
